import React, { useState, useEffect } from 'react';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  RefreshCw,
  Copy,
  Check,
  Key,
  Edit2,
  Save,
  X,
  User,
  UserPlus,
  Link2,
  Search,
  AlertCircle,
  Info
} from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../hooks/useToast';

const CompteManquantPanel = ({
  commercantId,
  commercantEmail,
  commercantNom,
  onAccountReady
}) => {
  const { toast } = useToast();
  const [mode, setMode] = useState('create');
  const [creating, setCreating] = useState(false);
  const [linking, setLinking] = useState(false);
  const [searching, setSearching] = useState(false);
  const [lookup, setLookup] = useState(null);
  const [linkEmail, setLinkEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [createForm, setCreateForm] = useState({
    prenom: '',
    nom: '',
    password: '',
    generatePassword: true,
    telephone: ''
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.prenom.trim() || !createForm.nom.trim()) {
      toast.error('Le nom et le prénom sont requis');
      return;
    }
    if (!createForm.generatePassword && createForm.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    try {
      setCreating(true);
      const result = await apiService.createCommercantUser(commercantId, {
        prenom: createForm.prenom.trim(),
        nom: createForm.nom.trim(),
        telephone: createForm.telephone.trim() || undefined,
        password: createForm.generatePassword ? undefined : createForm.password,
        generatePassword: createForm.generatePassword
      });
      toast.success('Compte commerçant créé avec succès');
      onAccountReady(result.user, result.password);
    } catch (error) {
      const code = error.data?.code || error.response?.data?.code;
      if (code === 'USER_ALREADY_EXISTS') {
        toast.error('Un compte existe déjà avec cet email — utilisez l\'onglet « Associer »');
        setMode('link');
        setLinkEmail(commercantEmail);
      } else {
        toast.error(error.message || 'Erreur lors de la création du compte');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleSearch = async () => {
    const email = linkEmail.trim().toLowerCase();
    if (!email) {
      toast.error('Saisissez un email à rechercher');
      return;
    }
    try {
      setSearching(true);
      setLookup(null);
      const result = await apiService.lookupCommercantUser(commercantId, email);
      setLookup(result);
      if (!result.found) {
        toast.info('Aucun utilisateur trouvé avec cet email');
      }
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la recherche');
    } finally {
      setSearching(false);
    }
  };

  const handleLink = async () => {
    if (!lookup?.user?.id) return;
    try {
      setLinking(true);
      const result = await apiService.linkCommercantUser(commercantId, lookup.user.id);
      toast.success(
        `${result.message || 'Compte associé'} — vous pouvez réinitialiser le mot de passe ci-dessous si besoin.`
      );
      onAccountReady(result.user, null);
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'association');
    } finally {
      setLinking(false);
    }
  };

  if (!commercantEmail) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-amber-900">Email de la boutique manquant</h3>
            <p className="mt-2 text-sm text-amber-800">
              Pour créer ou associer un compte, renseignez d&apos;abord l&apos;email de la boutique
              {commercantNom ? ` « ${commercantNom} »` : ''} dans les informations générales.
            </p>
            <p className="mt-2 text-xs text-amber-700">
              Le compte de connexion doit utiliser le même email que la boutique.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <Key className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-amber-900">Aucun compte de connexion</h3>
            <p className="mt-1 text-sm text-amber-800">
              Aucun utilisateur <strong>COMMERCANT</strong> n&apos;est lié à l&apos;email{' '}
              <span className="font-mono text-amber-900">{commercantEmail}</span>.
            </p>
            <p className="mt-2 text-xs text-amber-700 flex items-start gap-1">
              <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              Le compte doit partager l&apos;email de la boutique pour accéder à l&apos;admin commerçant.
            </p>
          </div>
        </div>
      </div>

      <div className="flex rounded-lg border border-gray-200 p-1 bg-gray-50">
        <button
          type="button"
          onClick={() => setMode('create')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md transition-colors ${
            mode === 'create'
              ? 'bg-white text-blue-700 shadow-sm border border-gray-200'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <UserPlus className="h-4 w-4" />
          Créer un compte
        </button>
        <button
          type="button"
          onClick={() => setMode('link')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md transition-colors ${
            mode === 'link'
              ? 'bg-white text-blue-700 shadow-sm border border-gray-200'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Link2 className="h-4 w-4" />
          Associer un compte existant
        </button>
      </div>

      {mode === 'create' ? (
        <form onSubmit={handleCreate} className="bg-white rounded-lg border shadow-sm p-6 space-y-4">
          <h4 className="text-base font-semibold text-gray-900">Nouveau compte commerçant</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email de connexion</label>
            <input
              type="email"
              value={commercantEmail}
              readOnly
              className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700 font-mono text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">Identique à l&apos;email de la boutique</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
              <input
                type="text"
                value={createForm.prenom}
                onChange={(e) => setCreateForm({ ...createForm, prenom: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Prénom du gérant"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
              <input
                type="text"
                value={createForm.nom}
                onChange={(e) => setCreateForm({ ...createForm, nom: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Nom du gérant"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone (optionnel)</label>
            <input
              type="tel"
              value={createForm.telephone}
              onChange={(e) => setCreateForm({ ...createForm, telephone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="border-t pt-4 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={createForm.generatePassword}
                onChange={(e) =>
                  setCreateForm({ ...createForm, generatePassword: e.target.checked })
                }
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Générer un mot de passe sécurisé automatiquement</span>
            </label>

            {!createForm.generatePassword && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    placeholder="Min. 8 caractères"
                    minLength={8}
                  />
                </div>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={creating}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            <UserPlus className={`h-4 w-4 ${creating ? 'animate-pulse' : ''}`} />
            {creating ? 'Création en cours...' : 'Créer le compte commerçant'}
          </button>
        </form>
      ) : (
        <div className="bg-white rounded-lg border shadow-sm p-6 space-y-4">
          <h4 className="text-base font-semibold text-gray-900">Associer un compte existant</h4>
          <p className="text-sm text-gray-600">
            Recherchez un utilisateur par email. Son compte sera converti en commerçant et lié à{' '}
            <span className="font-mono text-gray-800">{commercantEmail}</span>.
          </p>

          <div className="flex gap-2">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="email"
                value={linkEmail}
                onChange={(e) => setLinkEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleSearch())}
                placeholder="Email du compte à associer"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="button"
              onClick={handleSearch}
              disabled={searching}
              className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 disabled:opacity-50 flex items-center gap-2"
            >
              <Search className={`h-4 w-4 ${searching ? 'animate-spin' : ''}`} />
              Rechercher
            </button>
          </div>

          {lookup?.found && (
            <div className="rounded-lg border border-gray-200 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">
                    {lookup.user.prenom} {lookup.user.nom}
                  </p>
                  <p className="text-sm text-gray-600 font-mono">{lookup.user.email}</p>
                </div>
                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                  {lookup.user.role}
                </span>
              </div>

              {lookup.alreadyLinkedToThisShop && (
                <p className="text-sm text-green-700 bg-green-50 rounded p-2">
                  Ce compte est déjà associé à cette boutique.
                </p>
              )}

              {lookup.linkedToOtherShop && (
                <p className="text-sm text-red-700 bg-red-50 rounded p-2 flex gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  Ce compte est déjà lié à une autre boutique
                  {lookup.linkedCommercant?.nom ? ` (« ${lookup.linkedCommercant.nom} »)` : ''}.
                </p>
              )}

              {lookup.emailWillChange && !lookup.linkedToOtherShop && (
                <p className="text-sm text-amber-800 bg-amber-50 rounded p-2 flex gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  L&apos;email du compte sera mis à jour vers{' '}
                  <span className="font-mono">{commercantEmail}</span> pour correspondre à la boutique.
                </p>
              )}

              {lookup.canLink && (
                <button
                  type="button"
                  onClick={handleLink}
                  disabled={linking}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
                >
                  <Link2 className={`h-4 w-4 ${linking ? 'animate-pulse' : ''}`} />
                  {linking ? 'Association...' : 'Associer à cette boutique'}
                </button>
              )}
            </div>
          )}

          {lookup && !lookup.found && (
            <p className="text-sm text-gray-500 text-center py-2">
              Aucun compte trouvé. Créez-en un via l&apos;onglet « Créer un compte ».
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const IdentifiantsCommercant = ({ commercantId, commercantEmail, commercantNom }) => {
  const { toast } = useToast();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({ nom: '', prenom: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (commercantId) {
      fetchUser();
    }
  }, [commercantId]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCommercantUser(commercantId);

      if (response.user) {
        setUser(response.user);
        setProfileData({
          nom: response.user.nom || '',
          prenom: response.user.prenom || ''
        });
      } else {
        setUser(null);
      }
    } catch (error) {
      if (error.status === 404 || error.response?.status === 404) {
        setUser(null);
      } else {
        toast.error('Erreur lors de la récupération des identifiants');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccountReady = (newUser, password) => {
    setUser(newUser);
    setProfileData({
      nom: newUser.nom || '',
      prenom: newUser.prenom || ''
    });
    if (password) {
      setGeneratedPassword(password);
    }
  };

  const handleResetPassword = async (generate = false) => {
    if (!user) return;

    try {
      setResetting(true);
      const passwordToUse = generate ? null : newPassword;

      if (!generate && (!passwordToUse || passwordToUse.length < 8)) {
        toast.error('Le mot de passe doit contenir au moins 8 caractères');
        return;
      }

      const result = await apiService.resetUserPassword(user.id, passwordToUse, generate);

      if (result.success) {
        const password = result.password || passwordToUse;
        setGeneratedPassword(password);
        setNewPassword('');
        toast.success(
          generate
            ? 'Mot de passe généré avec succès !'
            : 'Mot de passe réinitialisé avec succès !'
        );
      }
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
      toast.error(error.message || 'Erreur lors de la réinitialisation du mot de passe');
    } finally {
      setResetting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copié dans le presse-papiers !');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEditProfile = () => {
    setEditingProfile(true);
    setProfileData({
      nom: user.nom || '',
      prenom: user.prenom || ''
    });
  };

  const handleCancelEditProfile = () => {
    setEditingProfile(false);
    setProfileData({
      nom: user.nom || '',
      prenom: user.prenom || ''
    });
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    if (!profileData.nom.trim() || !profileData.prenom.trim()) {
      toast.error('Le nom et le prénom sont requis');
      return;
    }

    try {
      setSavingProfile(true);

      const updatedUser = await apiService.updateUser(user.id, {
        nom: profileData.nom.trim(),
        prenom: profileData.prenom.trim()
      });

      setUser(updatedUser);
      setEditingProfile(false);
      toast.success('Informations du compte mises à jour avec succès !');
      await fetchUser();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      toast.error(error.message || 'Erreur lors de la mise à jour des informations');
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <CompteManquantPanel
        commercantId={commercantId}
        commercantEmail={commercantEmail}
        commercantNom={commercantNom}
        onAccountReady={handleAccountReady}
      />
    );
  }

  return (
    <div className="space-y-6">
      {generatedPassword && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <label className="block text-sm font-medium text-green-800 mb-2">
            Mot de passe à communiquer au commerçant
          </label>
          <div className="flex items-center space-x-2">
            <input
              type={showPassword ? 'text' : 'password'}
              value={generatedPassword}
              readOnly
              className="flex-1 px-3 py-2 border border-green-300 rounded-md bg-white text-gray-900 font-mono text-sm"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
            <button
              type="button"
              onClick={() => copyToClipboard(generatedPassword)}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md"
            >
              {copied ? <Check className="h-5 w-5 text-green-600" /> : <Copy className="h-5 w-5" />}
            </button>
          </div>
          <p className="mt-2 text-xs text-green-700">
            Copiez ce mot de passe maintenant — il ne sera plus affiché après actualisation.
          </p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Key className="h-5 w-5 mr-2 text-blue-600" />
          Identifiants de connexion
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <Mail className="h-4 w-4 mr-2" />
              Email de connexion
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={user.email}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900 font-mono text-sm"
              />
              <button
                type="button"
                onClick={() => copyToClipboard(user.email)}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                title="Copier l'email"
              >
                {copied ? <Check className="h-5 w-5 text-green-600" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Réinitialiser le mot de passe</h4>

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => handleResetPassword(true)}
                disabled={resetting}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${resetting ? 'animate-spin' : ''}`} />
                <span>Générer un mot de passe automatiquement</span>
              </button>

              <div className="flex items-start space-x-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ou définir un mot de passe personnalisé
                  </label>
                  <div className="flex items-center space-x-2">
                    <div className="relative flex-1">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Nouveau mot de passe (min. 8 caractères)"
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleResetPassword(false)}
                      disabled={resetting || !newPassword || newPassword.length < 8}
                      className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50"
                    >
                      {resetting ? 'Réinitialisation...' : 'Définir'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg border p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-900 flex items-center">
            <User className="h-4 w-4 mr-2 text-blue-600" />
            Informations du compte
          </h4>
          {!editingProfile && (
            <button
              type="button"
              onClick={handleEditProfile}
              className="flex items-center space-x-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
            >
              <Edit2 className="h-4 w-4" />
              <span>Modifier</span>
            </button>
          )}
        </div>

        {editingProfile ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={profileData.nom}
                  onChange={(e) => setProfileData({ ...profileData, nom: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Prénom</label>
                <input
                  type="text"
                  value={profileData.prenom}
                  onChange={(e) => setProfileData({ ...profileData, prenom: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <button
                type="button"
                onClick={handleCancelEditProfile}
                disabled={savingProfile}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
              >
                <X className="h-4 w-4" />
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {savingProfile ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Nom:</span>
              <span className="ml-2 font-medium">{user.nom || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-600">Prénom:</span>
              <span className="ml-2 font-medium">{user.prenom || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-600">Rôle:</span>
              <span className="ml-2 font-medium">{user.role}</span>
            </div>
            <div>
              <span className="text-gray-600">Statut:</span>
              <span
                className={`ml-2 font-medium ${
                  user.statut === 'ACTIF' ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {user.statut}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IdentifiantsCommercant;
